// src/components/ManageDealDrawer/useManageDealDrawer.ts

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './ManageDealDrawer.module.scss';
import { isEqual } from 'lodash';

// Redux hooks & actions (replace paths with your own)
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    createDeal,
    createInvestmentPreference,
    fetchSingleCompanyDetails,
    getDealData,
    getInvestmentPreference,
    getLenderDealData,
    getMetricsForDealCreation,
    setDealDrawer,
    updateCompany,
    updateInvestmentPreference,
} from '../../store/investor/action';
import {
    fetchAllAvailableProducts,
    fetchCompanySectorsList,
    getDealFormConfig,
    getWebsiteVerification,
} from '../../store/investor/apiHelpers';
import { getCompanyDealMetrics, getCompanyMetrics, updateCompanyMetrics } from '../../store/debtProducts/apiHelper_debtProducts';
import { saveMandateApplicants } from '../../store/investor/service';
import { fetchManagementProfilesV2 } from '../../store/dashboard/overview/overview_apiHelper';

// Utilities (replace paths with your own)
import {
    convertToFirstLetterCapital,
    flatten,
    getPlatformUserId,
    getSubdomain,
    isEmpty,
    isLenderType,
    linkedInRegex,
    unflatten,
} from '../../utils/utils';
import { getAuditedYear, getFinancialYear, getTimeStamp, initializeDatepickerDates } from '../../utils/dateUtils';
import { updateQueryParams } from '../../utils/searchParamUtils';
import { Mixpanel } from '../../utils/mixpanel';
import { useWindowUnloadEffect } from '../../utils/constants/hooks';
import { EstimateButtonIcon } from '../../assets/Svg/EstimateButtonIcon';

// Constants
import {
    AICA_COMPANY_PROFILE,
    AICA_CREATE_DEAL_DETAILS,
    AICA_GET_COMPANY_SECTOR_LIST,
    AICA_INVESTMENT_PREFERENCE,
    AICA_PARTNER_PROFILE,
    AICA_YOUR_PROFILE,
    BUSINESS_DETAILS_FIELD_KEYS,
    BUSINESS_DETAILS_KEY,
    CONFIG_STEP,
    CREATE_INVESTMENT_PREFERENCE,
    DATA_SOURCE_TYPE,
    DEAL_DRAWER_ICON_MAP,
    DEBT_PRODUCT_FILTERS,
    DEFAULT_OWNER_TYPE,
    FY_END_DATE,
    MANDATE_APPLICANT_DESIGNATION_LABELS,
    MANDATE_APPLICANT_FIELD_KEYS,
    MANDATE_APPLICANT_SAVE_ERROR_PREFIX,
    MANDATE_APPLICANT_TYPE_KEY,
    MANDATE_APPLICATION_FORM,
    METADATA_KEYS_FOR_BORROWER_FINANCIALS,
    ORG_USERS_TABLE,
    PEC_STATUS,
    PINCODE_DIGIT_LENGTH,
    PROFILE,
    PROFILE_BUTTON,
    PROFILE_CUSTOM_FIELD_KEYS,
    USER_TYPE,
} from './DealDrawerConstants';
import {
    APPLICANT_LINK_SOURCE,
    ApplicantDirectorLink,
    buildMandateApplicantPeople,
    CoApplicantRow,
    ExistingDirectorOption,
    findDuplicateApplicant,
    getApplicantPresenceCounts,
    isApplicantEntryPresent,
    isApplicantEntryValid,
    resolveApplicantPrimaryTypeError,
    resolveSavedApplicantLinkId,
    resolveSavedPermanentSameAsCurrent,
    stripMaskedPhone,
    shouldBlockOnResponse,
    splitApplicantCardsByType,
} from './Components/Sections/coApplicantTypes';
import { hasBusinessDetailsData, isBusinessDetailsValid } from './Components/Sections/businessDetailsTypes';
import {
    buildFilteredSectionConfig,
    buildPickerOptions,
    expandFieldKeys,
    getAlwaysOnFieldKeys,
    resolveSelectedFields,
    selectionStorageKey,
} from './pecFieldSelection';
import { fetchOrgInfoById } from '../../store/investor/investorApiHelper';
import { setDebtProductsFilters } from '../../store/company/action';
import { AUDITED_FINANCIAL_KEY_METRIC } from '../../modules/DebtProducts/DebtProductsConstants';
import { fetchDataByConfig, fetchAllLendersWithConnections } from './ManageDealDrawerService';
import { convertToWordsWithUnit } from '../../utils/numberToWordUtil';
import { USER_TYPES } from '../../store/investor/constants';
import { polusBaseUrl } from '../../utils/axios-interceptor';

interface UseManageDealDrawerProps {
    source: string;
    onClose?: Function;
    dealId?: string;
    saveSelection?: Function;
    fetchCompanyMetrics?: Function;
    dealStatus?: string;
}

export function useManageDealDrawer({ source, onClose, dealId, saveSelection, fetchCompanyMetrics, dealStatus }: UseManageDealDrawerProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const urlParams = useParams();
    const [params, setParams] = useSearchParams();

    // Redux states
    const investorData = useAppSelector((state) => state.investor);
    const loggedInUser = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const user = useAppSelector((state) => state.user);
    const userData = user?.userData;

    // Local states
    const [formConfig, setFormConfig] = useState<any>(null);
    const [stepperConfig, setStepperConfig] = useState<any>(null);
    const [formData, setFormData] = useState<{ [key: string]: any }>({});
    const [initialFormData, setInitialFormData] = useState<{ [key: string]: any }>({});
    const [step, setStep] = useState<number>(investorData?.dealDrawer?.selectedStep ?? 1);
    const [disablePrimary, setDisablePrimary] = useState<boolean>(true);
    const [disableSecondary, setDisableSecondary] = useState<boolean>(step === 1);
    const [lenderList, setLenderList] = useState<{ id: string; name: string }[]>([]);
    const [preFilledDataFetched, setPreFilledDataFetched] = useState<boolean>(false);
    const [loadingPrefilledData, setLoadingPrefilledData] = useState<boolean>(false);
    const [animationClass, setAnimationClass] = useState<string>('');
    const [investorName, setInvestorName] = useState('');
    const [displayOrgName, setDisplayOrgName] = useState('');
    const [investmentPreferenceId, setInvestmentPreferenceId] = useState<string | null>(null);
    const [isFromComplete, setIsFromComplete] = useState<boolean>(false);
    const [restrictedMap, setRestrictedMap] = useState<Map<string, boolean>>(new Map());
    const [loanTypeOptions, setLoanTypeOptions] = useState<any>({});
    const [loanTypeNameToIdMap, setLoanTypeNameToIdMap] = useState(new Map<string, string>());
    const [auditedfinancialsMapping, setAuditedfinancialsMapping] = useState<Record<string, { value: string | number | null }>>({});
    const debtProductFilters = useAppSelector((state) => state.company.debtProductsFilters);
    const [duplicate, setDuplicate] = useState(false);
    const [filterType, setFilterType] = useState('');
    const [sectorList, setSectorList] = useState<any[]>([]);
    const [negativeSectorList, setNegativeSectorList] = useState<any[]>([]);
    const [isWebsiteValid, setIsWebsiteValid] = useState(true);
    const [isLinkedInValid, setIsLinkedInValid] = useState(true);
    const [isWebsiteVerificationLoading, setIsWebsiteVerificationLoading] = useState(false);
    const [isWebsitePrefilled, setIsWebsitePrefilled] = useState(false);
    const [negativeLenderList, setNegativeLenderList] = useState<any[]>([]);
    const [connectedLenderNames, setConnectedLenderNames] = useState<string[]>([]);
    const [activeFinancialsTab, setActiveFinancialsTab] = useState<string>('');

    // Dismissal alert state
    const [showDismissalAlert, setShowDismissalAlert] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState<{ [key: string]: any }>({});
    const [isFormInitialized, setIsFormInitialized] = useState(false);

    const [citiesList, SetCitiesList] = useState<{ label: string; value: string }[]>([]);
    const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    // Application Form drawer (MANDATE_APPLICATION_FORM source) - primary applicant + co-applicant
    // roster for the mandate/deal this drawer instance was opened against.
    const [coApplicants, setCoApplicants] = useState<CoApplicantRow[]>([]);
    const coApplicantIdCounterRef = useRef(0);
    const [primaryApplicantLink, setPrimaryApplicantLink] = useState<ApplicantDirectorLink>({});
    // Whether the Primary Applicant card has been spawned yet (Figma: cards are spawned by the
    // "Select Applicants" picker/Add, not statically pre-rendered) - true once hydration finds a
    // saved primary, or once the picker/Add fills the primary slot for the first time.
    const [primarySlotActive, setPrimarySlotActive] = useState(false);
    const [existingDirectors, setExistingDirectors] = useState<ExistingDirectorOption[]>([]);
    const [isSavingMandateApplicants, setIsSavingMandateApplicants] = useState(false);

    const subDomain = getSubdomain();
    const userOrgId = sessionStorage.getItem('user_org_id') ?? localStorage.getItem('user_org_id') ?? '';
    const isLender = isLenderType(userData?.accountType);
    const isPartner = user.userData.accountType === USER_TYPE.PARTNER;
    const flowId = 16;
    const formDataRef = useRef<{}>({});

    const BORROWER_PROFILE_CONSTANTS = {
        WEBSITE: 'borrowerProfile.website',
        LINKEDIN: 'borrowerProfile.linkedin',
        // Unprefixed key, for use against a nested borrowerProfile object rather than
        // the flattened formData map.
        LINKEDIN_KEY: 'linkedin',
    };
    const UNIT_CONSTANTS = {
        INR_CR: 'INR CR',
    };

    // Refs
    const dealDrawerBodyRef = useRef<any>(null);
    const stepContainerRef = useRef<any>(null);

    // -------------------------------------
    // REUSABLE FUNCTIONS / SIDE EFFECTS
    // -------------------------------------

    // 1. Create a map of required form keys by scanning the entire `formConfig`.
    const createMapOfRequiredVsSectionKeys = () => {
        const requiredMap = new Map<string, boolean>();
        if (!formConfig) return;

        for (const currentStepConfig of formConfig) {
            for (const config of currentStepConfig.config) {
                const sectionKey = config?.sectionKey;
                for (const key in config?.config) {
                    const fieldConfig = config.config[key];
                    let formKey = sectionKey ? `${sectionKey}.${key}` : key;

                    if (fieldConfig.isRequired) {
                        if (fieldConfig.type === 'range') {
                            let minKey = `minimum${convertToFirstLetterCapital(key)}`;
                            let maxKey = `maximum${convertToFirstLetterCapital(key)}`;
                            if (sectionKey) {
                                minKey = `${sectionKey}.${minKey}`;
                                maxKey = `${sectionKey}.${maxKey}`;
                            }
                            requiredMap.set(minKey, true);
                            requiredMap.set(maxKey, true);
                        } else {
                            requiredMap.set(formKey, true);
                        }
                    }
                }
            }
        }
        setRestrictedMap(requiredMap);
    };

    const getConvertedValue = (value: string, unit: string) => {
        if (!value) return '';
        let convertedWord = '';
        switch (unit) {
            case UNIT_CONSTANTS.INR_CR:
                convertedWord = convertToWordsWithUnit(Number(value), 'CR');
                break;
            default:
                break;
        }
        return convertedWord;
    };

    // RECUR-21135: shared Brand Name (editable, OrganisationMaster.displayOrgName) + Registered
    // Name (read-only, OrganisationMaster.orgName) prefill — used by both the logged-in user's own
    // profile edit (PROFILE_BUTTON/ORG_USERS_TABLE) and the assessee "Edit Profile" (Debt Products)
    // so the two entry points for this same OrganisationMaster data can't drift apart again.
    const applyBrandPrefill = (borrowerProfileObj: Record<string, any>, orgInfo: any) => {
        borrowerProfileObj.displayOrgName = orgInfo?.orgDisplayName ?? '';
        borrowerProfileObj.orgName = orgInfo?.orgName ?? '';
    };

    // 2. Check if the entire form is complete by comparing `formData` with `restrictedMap`.
    const checkCompleteForm = () => {
        let isValid = true;
        for (const [key] of restrictedMap) {
            if (!formData[key]) {
                isValid = false;
                break;
            }
        }
        setIsFromComplete(isValid);
    };

    const validateWebsite = async (url: string) => {
        if (!url || step != 2) return;
        setDisablePrimary(true);
        setDisableSecondary(true);
        setIsWebsiteVerificationLoading(true);
        const payload = {
            websiteUrl: url,
            skipCin: true,
        };
        const response = await getWebsiteVerification(payload);
        if (response?.redirectDomain) {
            setIsWebsiteValid(true);
        } else {
            setIsWebsiteValid(false);
        }
        setDisablePrimary(!response.redirectDomain || !isLinkedInValid);
        setDisableSecondary(!response.redirectDomain || !isLinkedInValid);
        setIsWebsiteVerificationLoading(false);
    };

    // 3. Validate the current step (enable/disable primary button).
    const validateForm = () => {
        if (!formConfig) return;
        const currentStepConfig = formConfig?.[step - 1];
        if (!currentStepConfig) return;

        // Application Form drawer: Proceed is gated purely on Name+PAN validity for every
        // PRESENT person (primary + co-applicants), not the generic required-field check below -
        // an empty applicant section is valid (Proceed stays enabled, the save is skipped
        // entirely). The existing-director dropdown applies its pick immediately (no separate
        // "uncommitted selection" state to gate on).
        if (source === MANDATE_APPLICATION_FORM) {
            const flattenedFormData = unflatten(formData);
            const primaryFields = flattenedFormData?.[CONFIG_STEP.primaryApplicant] || {};
            const isPrimaryValid = !isApplicantEntryPresent(primaryFields) || isApplicantEntryValid(primaryFields);
            const areCoApplicantsValid = coApplicants
                .filter((row) => isApplicantEntryPresent(row))
                .every((row) => isApplicantEntryValid(row));
            // Exactly one Primary Applicant is required once any applicant is present; a fully
            // empty roster (BD-only save) needs none - see resolveApplicantPrimaryTypeError.
            const { totalPresentCount, primaryMarkedCount } = getApplicantPresenceCounts(primaryFields, coApplicants);
            const isPrimaryTypeValid = totalPresentCount === 0 || primaryMarkedCount === 1;
            // Fast pre-check: two present cards must not resolve to the same person (same PAN or
            // same picked director) - the backend's directorId-collision guard is authoritative.
            const isRosterUnique = !findDuplicateApplicant(primaryFields, primaryApplicantLink, coApplicants);
            const businessDetails = flattenedFormData?.[BUSINESS_DETAILS_KEY] || {};
            setDisablePrimary(
                !(isPrimaryValid && areCoApplicantsValid && isPrimaryTypeValid && isRosterUnique && isBusinessDetailsValid(businessDetails)) ||
                    isSavingMandateApplicants,
            );
            setDisableSecondary(false);
            return;
        }

        const currentStepKeys: string[] = [];
        for (const config of currentStepConfig?.config ?? []) {
            const sectionKey = config?.sectionKey;
            for (const key in config.config) {
                const fieldConfig = config.config[key];
                if (!fieldConfig.isRequired) continue;

                let formKey = sectionKey ? `${sectionKey}.${key}` : key;

                // if it's a range
                if (fieldConfig.type === 'range') {
                    let minKey = `minimum${convertToFirstLetterCapital(key)}`;
                    let maxKey = `maximum${convertToFirstLetterCapital(key)}`;
                    let importanceKey = `importance${convertToFirstLetterCapital(key)}`;
                    if (sectionKey) {
                        minKey = `${sectionKey}.${minKey}`;
                        maxKey = `${sectionKey}.${maxKey}`;
                        importanceKey = `${sectionKey}.${importanceKey}`;
                    }
                    currentStepKeys.push(minKey, maxKey);
                    if (fieldConfig.importance) {
                        currentStepKeys.push(importanceKey);
                    }
                }
                // if there's "importance"
                else if (fieldConfig.importance) {
                    let importanceKey = `importance${convertToFirstLetterCapital(key)}`;
                    if (sectionKey) {
                        importanceKey = `${sectionKey}.${importanceKey}`;
                    }
                    currentStepKeys.push(formKey, importanceKey);
                } else {
                    currentStepKeys.push(formKey);
                }
            }
        }
        // all values must be present in formData
        const isFormValid = currentStepKeys.every((key) => key in formData && !isEmpty(formData[key])) && isWebsiteValid && isLinkedInValid;
        setDisablePrimary(!isFormValid);
        setDisableSecondary(!isFormValid);
    };

    useEffect(() => {
        if (isWebsitePrefilled) {
            setIsWebsiteValid(true);
        } else {
            setIsWebsiteValid(false);
        }
    }, [formData[BORROWER_PROFILE_CONSTANTS.WEBSITE]]);

    useEffect(() => {
        validateForm();
    }, [isWebsiteValid]);

    useEffect(() => {
        if (formData[BORROWER_PROFILE_CONSTANTS.WEBSITE]) {
            setIsWebsitePrefilled(true);
        }
    }, [step]);

    const getButtonText = (): string => {
        if (isWebsiteVerificationLoading) return '';
        if (isWebsiteValid) {
            return formData[BORROWER_PROFILE_CONSTANTS.WEBSITE] ? 'Verified' : 'Verify';
        }
        return formData[BORROWER_PROFILE_CONSTANTS.WEBSITE] ? 'Verify' : '';
    };

    const getButtonIcon = (buttonImgName: string): JSX.Element => {
        if (buttonImgName === 'VERIFY_WEBSITE') {
            if (isWebsiteVerificationLoading) {
                return React.cloneElement(DEAL_DRAWER_ICON_MAP.VERIFY_WEBSITE.LOADING, {
                    className: styles.customFetch,
                });
            }
            if ((isWebsiteValid && isEmpty(formData[BORROWER_PROFILE_CONSTANTS.WEBSITE])) || !isWebsiteValid) {
                return React.cloneElement(DEAL_DRAWER_ICON_MAP.VERIFY_WEBSITE.INVALID, {
                    className: styles.customFetch,
                });
            }
            return React.cloneElement(DEAL_DRAWER_ICON_MAP.VERIFY_WEBSITE.VALID, {
                className: styles.customFetch,
            });
        }
        const icon = DEAL_DRAWER_ICON_MAP[buttonImgName];
        if (typeof icon === 'string') {
            return <img src={icon} width={'14'} height={'14'} />;
        }
        if (React.isValidElement(icon)) {
            return React.cloneElement(icon);
        }
        return <></>;
    };

    // 4. Close the drawer
    const closeDrawer = (payload?: any) => {
        params.delete('investmentPreferenceId');
        params.delete('Duplicate');
        updateQueryParams(params, setParams, {});
        dispatch(setDealDrawer({ open: false }));
        if (source === DEBT_PRODUCT_FILTERS) {
            params.set('filters', 'true');
            updateQueryParams(params, setParams, {});
            onClose?.(payload ?? debtProductFilters);
        } else {
            onClose?.(debtProductFilters);
        }
    };

    const handleDrawerClose = () => {
        if (hasUnsavedChanges) setShowDismissalAlert(true);
        else closeDrawer();
    };

    const handleSaveAndClose = async () => {
        setShowDismissalAlert(false);
        await handlePrimary();
        closeDrawer();
    };

    const handleCloseWithoutSave = () => {
        setShowDismissalAlert(false);
        closeDrawer();
    };

    const hasFinancialDataInNestedObject = (obj: any): boolean => {
        if (!obj || typeof obj !== 'object') return false;
        return Object.keys(obj)
            .filter((key) => !METADATA_KEYS_FOR_BORROWER_FINANCIALS.includes(key))
            .some((key) => {
                const value = obj[key];
                return value !== '' && value !== null && value !== undefined;
            });
    };

    // 5. Generic handle change
    const handleChange = (key: string, value: any, importanceKey?: string | null) => {
        // in case the value is empty we need to delete the key
        if (dealStatus === 'DRAFT_DELETED' && !params.has('draftDeleteUpdated')) {
            updateQueryParams(params, setParams, { draftDeleteUpdated: true }, location);
        }

        if (value === '' || value === null) {
            setFormData((prev) => {
                const updatedData = { ...prev };
                delete updatedData[key];
                importanceKey && delete updatedData[importanceKey];
                return updatedData;
            });
        } else {
            setFormData((prev) => ({ ...prev, [key]: value }));
        }
    };

    // -------------------------------------
    // 5a. PEC per-step field selection (the picker-controlled render set)
    // -------------------------------------
    const isPec = !!investorData?.dealDrawer?.isPec;
    const currentStepSections = useMemo(() => formConfig?.[step - 1]?.config ?? [], [formConfig, step]);

    // A PEC page carries exactly one section with a selection storage key; any other section on
    // the page keeps rendering unfiltered.
    const pecStepSection = useMemo(
        () => (isPec ? currentStepSections.find((section: any) => !!selectionStorageKey(section?.sectionKey)) ?? null : null),
        [isPec, currentStepSections],
    );
    const pecStorageKey = selectionStorageKey(pecStepSection?.sectionKey);
    const storedStepSelection = pecStorageKey ? formData[pecStorageKey] : undefined;

    const pecAlwaysOnFields = useMemo(() => (pecStepSection ? getAlwaysOnFieldKeys(pecStepSection) : []), [pecStepSection]);

    // Keyed on the stored array's identity, never on formData: the selection only changes when
    // that array is replaced, and a formData-keyed memo would hand FormSection a fresh section
    // object — and so re-run its validation effect — on every keystroke.
    const pecSelectedFields = useMemo(
        () => (pecStepSection ? resolveSelectedFields(pecStepSection, formData) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pecStepSection, storedStepSelection],
    );

    const pecPickerOptions = useMemo(() => (pecStepSection ? buildPickerOptions(pecStepSection) : []), [pecStepSection]);

    const pecFilteredSection = useMemo(
        () => (pecStepSection ? buildFilteredSectionConfig(pecStepSection, pecSelectedFields, pecAlwaysOnFields) : null),
        [pecStepSection, pecSelectedFields, pecAlwaysOnFields],
    );

    // Always-on fields keep a step non-empty, so the empty state is reachable only where the
    // config declares no required field.
    const isStepConfigured = !pecStepSection || pecSelectedFields.length + pecAlwaysOnFields.length > 0;

    const expandStepFieldKeys = (fieldKey: string): string[] =>
        pecStepSection ? expandFieldKeys(pecStepSection.sectionKey ?? '', fieldKey, pecStepSection.config?.[fieldKey]) : [];

    const pecRemovedValuesStashRef = useRef<Record<string, Record<string, any>>>({});

    // Dropping a field's keys from formData is what produces its null tombstones on save: keys
    // that never held a value are absent from initialFormData, so no tombstone is invented for them.
    const applyStepSelection = (nextSelection: string[], fieldsToClear: string[], fieldsToRestore: string[] = []) => {
        if (!pecStorageKey) return;
        const stash = pecRemovedValuesStashRef.current;
        fieldsToClear.forEach((fieldKey) => {
            const values: Record<string, any> = {};
            expandStepFieldKeys(fieldKey).forEach((key) => {
                if (formData[key] !== undefined) values[key] = formData[key];
            });
            if (Object.keys(values).length) stash[`${pecStorageKey}|${fieldKey}`] = values;
        });
        const restores: Record<string, any> = {};
        fieldsToRestore.forEach((fieldKey) => {
            const values = stash[`${pecStorageKey}|${fieldKey}`];
            if (!values) return;
            Object.assign(restores, values);
            delete stash[`${pecStorageKey}|${fieldKey}`];
        });
        const keysToClear = fieldsToClear.flatMap(expandStepFieldKeys);
        setFormData((prev) => {
            const updatedData = { ...prev };
            keysToClear.forEach((key) => delete updatedData[key]);
            Object.entries(restores).forEach(([key, value]) => {
                if (updatedData[key] === undefined) updatedData[key] = value;
            });
            updatedData[pecStorageKey] = nextSelection;
            return updatedData;
        });
    };

    // The picker applies immediately: every tick replaces the step's selection. Unticking a
    // field removes it (values stashed); re-ticking it in the same session restores them.
    const handlePickerSelectionChange = (nextSelection: string[]) => {
        const cleanNext = nextSelection.filter((fieldKey) => !pecAlwaysOnFields.includes(fieldKey));
        applyStepSelection(
            cleanNext,
            pecSelectedFields.filter((fieldKey) => !nextSelection.includes(fieldKey)),
            cleanNext.filter((fieldKey) => !pecSelectedFields.includes(fieldKey)),
        );
    };

    const handleClearField = (fieldKey: string) => {
        const keysToClear = expandStepFieldKeys(fieldKey);
        if (!keysToClear.length) return;
        setFormData((prev) => {
            const updatedData = { ...prev };
            keysToClear.forEach((key) => delete updatedData[key]);
            return updatedData;
        });
    };

    const handleRemoveAllForStep = () => applyStepSelection([], pecSelectedFields);

    // Clear All keeps the rows on screen and only blanks their values — always-on fields
    const handleClearAllForStep = () => {
        const keysToClear = [...pecSelectedFields, ...pecAlwaysOnFields].flatMap(expandStepFieldKeys);
        if (!keysToClear.length) return;
        setFormData((prev) => {
            const updatedData = { ...prev };
            keysToClear.forEach((key) => delete updatedData[key]);
            return updatedData;
        });
    };

    // Seed the resolved selection into formData once per opened PEC, before the 500ms
    // originalFormData snapshot, so the drawer does not open already claiming unsaved changes.
    const pecSelectionSeededRef = useRef(false);
    useEffect(() => {
        if (!isPec || !formConfig || pecSelectionSeededRef.current) return;
        const prefId = investmentPreferenceId || params.get('investmentPreferenceId');
        // An existing PEC's selection may have to be derived from its saved values — wait for them.
        if (prefId && isEmpty(initialFormData)) return;
        pecSelectionSeededRef.current = true;
        setFormData((prev) => {
            const updatedData = { ...prev };
            for (const page of formConfig) {
                for (const section of page?.config ?? []) {
                    const storageKey = selectionStorageKey(section?.sectionKey);
                    if (!storageKey) continue;
                    updatedData[storageKey] = resolveSelectedFields(section, prev);
                }
            }
            return updatedData;
        });
    }, [isPec, formConfig, initialFormData, investmentPreferenceId, params]);

    // Handle tab change for borrower financials (kept for backward compatibility)
    const handleFinancialsTabChange = (tab: string) => {
        if (!tab || tab === activeFinancialsTab) return;
        setActiveFinancialsTab(tab);
    };

    // 6. Move to next / previous step
    const navigateStep = (newStep: number, animation: string) => {
        setAnimationClass(animation);
        setTimeout(() => {
            setStep(newStep);
        }, 300);
    };

    const continueToNextStep = () => {
        if (step === formConfig?.length) {
            onClose?.();
        } else {
            navigateStep(step + 1, 'slide-down');
        }
    };

    const flowToMandate = (currentFormData: any, initialFormData: any): boolean => {
        const keysToCompare = ['dealTerms.maximumDealAmount', 'dealTerms.roi', 'dealTerms.tenure', 'dealTerms.securityOffered'];

        return keysToCompare.some((key) => currentFormData[key] !== initialFormData[key]);
    };

    // -------------------------------------
    // Application Form drawer (MANDATE_APPLICATION_FORM source)
    // -------------------------------------

    const addCoApplicantRow = () => {
        coApplicantIdCounterRef.current += 1;
        setCoApplicants((prev) => [
            ...prev,
            {
                id: `newCoApplicant-${coApplicantIdCounterRef.current}`,
                persisted: false,
                [MANDATE_APPLICANT_TYPE_KEY]: MANDATE_APPLICANT_DESIGNATION_LABELS.CO_APPLICANT_LABEL,
            },
        ]);
    };

    const removeCoApplicantRow = (id: string) => {
        setCoApplicants((prev) => prev.filter((row) => row.id !== id));
    };

    // Mirrors handleChange's semantics: clearing a field also clears its associated importance value.
    const updateCoApplicantField = (id: string, fieldKey: string, value: any, importanceKey?: string | null) => {
        setCoApplicants((prev) =>
            prev.map((row) => {
                if (row.id !== id) return row;
                const updatedRow = { ...row, [fieldKey]: value };
                if ((value === '' || value === null) && importanceKey) delete updatedRow[importanceKey];
                return updatedRow;
            }),
        );
    };

    // Clears the primary applicant slot entirely (fields + link) - used when the "Select
    // Applicants" picker deselects the director currently linked to the primary card, mirroring
    // removeCoApplicantRow's full-removal semantics for a co-applicant row.
    const clearPrimaryApplicant = () => {
        setPrimarySlotActive(false);
        setPrimaryApplicantLink({});
        setFormData((prev) => {
            const updatedData = { ...prev };
            Object.values(MANDATE_APPLICANT_FIELD_KEYS).forEach((key) => {
                delete updatedData[`${CONFIG_STEP.primaryApplicant}.${key}`];
            });
            // Not in MANDATE_APPLICANT_FIELD_KEYS (UI-state-only) - the loop above misses it.
            delete updatedData[`${CONFIG_STEP.primaryApplicant}.${MANDATE_APPLICANT_TYPE_KEY}`];
            return updatedData;
        });
    };

    // Creates a new co-applicant row already linked to an existing director - the picker spawns a
    // fresh card per pick rather than converting an existing blank one (see spawnApplicantCard).
    const addLinkedCoApplicantRow = (director: ExistingDirectorOption) => {
        coApplicantIdCounterRef.current += 1;
        setCoApplicants((prev) => [
            ...prev,
            {
                id: `newCoApplicant-${coApplicantIdCounterRef.current}`,
                persisted: false,
                [MANDATE_APPLICANT_FIELD_KEYS.NAME]: director.name,
                [MANDATE_APPLICANT_FIELD_KEYS.PAN]: director.pan || '',
                // Never prefill a PII-masked phone (see stripMaskedPhone) - phone is editable
                // for a linked director, so leave it blank for the user to type a real number.
                [MANDATE_APPLICANT_FIELD_KEYS.PHONE]: stripMaskedPhone(director.phone) || '',
                [MANDATE_APPLICANT_FIELD_KEYS.EMAIL]: director.email || '',
                [MANDATE_APPLICANT_TYPE_KEY]: MANDATE_APPLICANT_DESIGNATION_LABELS.CO_APPLICANT_LABEL,
                managementProfileId: director.managementProfileId,
                directorId: director.directorId || null,
                linkSource: APPLICANT_LINK_SOURCE.DROPDOWN,
            },
        ]);
    };

    // The first card spawned (via the picker or Add) always fills the Primary Applicant slot;
    // every subsequent one becomes a new Co-Applicant row. `director` is null for a manual Add.
    const spawnApplicantCard = (director: ExistingDirectorOption | null) => {
        if (!primarySlotActive) {
            setPrimarySlotActive(true);
            const prefix = `${CONFIG_STEP.primaryApplicant}.`;
            handleChange(`${prefix}${MANDATE_APPLICANT_TYPE_KEY}`, MANDATE_APPLICANT_DESIGNATION_LABELS.PRIMARY);
            if (director) {
                handleChange(`${prefix}${MANDATE_APPLICANT_FIELD_KEYS.NAME}`, director.name);
                handleChange(`${prefix}${MANDATE_APPLICANT_FIELD_KEYS.PAN}`, director.pan || '');
                // Never prefill a PII-masked phone (see stripMaskedPhone) - phone is editable
                // for a linked director, so leave it blank for the user to type a real number.
                handleChange(`${prefix}${MANDATE_APPLICANT_FIELD_KEYS.PHONE}`, stripMaskedPhone(director.phone) || '');
                handleChange(`${prefix}${MANDATE_APPLICANT_FIELD_KEYS.EMAIL}`, director.email || '');
                setPrimaryApplicantLink({
                    managementProfileId: director.managementProfileId,
                    directorId: director.directorId || null,
                    linkSource: APPLICANT_LINK_SOURCE.DROPDOWN,
                });
            }
            return;
        }
        if (director) addLinkedCoApplicantRow(director);
        else addCoApplicantRow();
    };

    const handleAddApplicant = () => spawnApplicantCard(null);

    // managementProfileId of every director currently linked to a card (primary or co-applicant) -
    // what the "Select Applicants" multi-select shows as its selected chips.
    const applicantPickerSelectedIds = useMemo(
        () => [primaryApplicantLink.managementProfileId, ...coApplicants.map((row) => row.managementProfileId)].filter(Boolean) as string[],
        [primaryApplicantLink.managementProfileId, coApplicants],
    );

    // Inline one-Primary-Applicant banner shown under the picker - mirrors the same gate
    // validateForm applies to disablePrimary, so both agree on when the roster is invalid.
    const applicantPrimaryTypeError = useMemo(() => {
        if (source !== MANDATE_APPLICATION_FORM) return '';
        const primaryFields = unflatten(formData)?.[CONFIG_STEP.primaryApplicant] || {};
        const { totalPresentCount, primaryMarkedCount } = getApplicantPresenceCounts(primaryFields, coApplicants);
        return resolveApplicantPrimaryTypeError(totalPresentCount, primaryMarkedCount);
    }, [source, formData, coApplicants]);

    // Inline duplicate-person banner - mirrors the same isRosterUnique gate validateForm applies
    // to disablePrimary, so both agree on when the roster has two cards for the same person.
    const duplicateApplicantError = useMemo(() => {
        if (source !== MANDATE_APPLICATION_FORM) return '';
        const primaryFields = unflatten(formData)?.[CONFIG_STEP.primaryApplicant] || {};
        return findDuplicateApplicant(primaryFields, primaryApplicantLink, coApplicants);
    }, [source, formData, primaryApplicantLink, coApplicants]);

    // The multi-select reports its FULL next selection; diff against the current one to spawn a
    // card per newly-picked director and remove the card behind every deselected one.
    const handleApplicantPickerChange = (nextSelectedIds: (string | null)[]) => {
        const addedIds = nextSelectedIds.filter((id) => id && !applicantPickerSelectedIds.includes(id));
        const removedIds = applicantPickerSelectedIds.filter((id) => !nextSelectedIds.includes(id));

        // spawnApplicantCard reads primarySlotActive from the (stale-until-next-render) closure, so
        // track slot consumption locally: the first added director fills primary, the rest co-applicant.
        let primarySlotClaimed = primarySlotActive;
        addedIds.forEach((id) => {
            const director = existingDirectors.find((d) => d.managementProfileId === id);
            if (!director) return;
            if (primarySlotClaimed) {
                addLinkedCoApplicantRow(director);
            } else {
                primarySlotClaimed = true;
                spawnApplicantCard(director);
            }
        });
        removedIds.forEach((id) => {
            if (primaryApplicantLink.managementProfileId === id) {
                clearPrimaryApplicant();
                return;
            }
            const row = coApplicants.find((r) => r.managementProfileId === id);
            if (row) removeCoApplicantRow(row.id);
        });
    };

    // Full per-mandate detail (every §4 field) for primaryApplicant/coApplicants[] in the save
    // payload. When "permanent same as current" is ticked, the permanent fields are hidden in
    // the UI and must mirror current* regardless of whatever stale value is still on the row.
    // managementProfileId is the stable link identity persisted into the Deal detail (the bureau
    // directorId may be absent). polus passes the detail through untouched, so carrying it here is
    // what lets a reopened row rehydrate as RETURNING_EDIT rather than unlocking Name/PAN.
    const buildApplicantDetail = (fields: Record<string, any>, clientRef: string, managementProfileId?: string | null) => {
        const detail: Record<string, any> = { clientRef };
        Object.values(MANDATE_APPLICANT_FIELD_KEYS).forEach((key) => {
            // UI-only toggle (never persisted on its own) - only the mirrored permanent* values
            // below are saved.
            if (key === MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_SAME_AS_CURRENT) return;
            detail[key] = fields[key];
        });
        // A masked phone prefilled from a linked director must not be persisted into
        // Deal.customFields.* either - see stripMaskedPhone.
        detail[MANDATE_APPLICANT_FIELD_KEYS.PHONE] = stripMaskedPhone(fields[MANDATE_APPLICANT_FIELD_KEYS.PHONE]);
        if (fields[MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_SAME_AS_CURRENT]) {
            detail[MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_ADDRESS] = fields[MANDATE_APPLICANT_FIELD_KEYS.CURRENT_ADDRESS];
            detail[MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_ADDRESS_OWNERSHIP] = fields[MANDATE_APPLICANT_FIELD_KEYS.CURRENT_ADDRESS_OWNERSHIP];
        }
        if (managementProfileId) detail.managementProfileId = managementProfileId;
        return detail;
    };

    // Best-effort auto-populate: mirrors the newly captured business pincode into
    // companyMetrics.borrowerProfile so downstream underwriting reads pick it up. Swallows its
    // own errors so it can never block Proceed - Deal.customFields.businessDetails (saved by
    // handleSaveMandateApplicants above) is already the save of record for this pincode.
    const syncBusinessPincodeToBorrowerProfile = async (orgId: string | undefined, pincode: string | undefined) => {
        if (!orgId || pincode?.length !== PINCODE_DIGIT_LENGTH || !/^\d+$/.test(pincode)) return;
        try {
            // suppressToast - a background sync failing shouldn't surface a "some error occurred"
            // toast right after the applicant save just succeeded.
            // dataSource: BOTH keeps borrowerFinancials nested ({ actualData, discussion, dataSource })
            // on the round trip - omitting it makes the backend flatten borrowerFinancials to the
            // active source's fields, and POSTing that back wipes the other source's data.
            const metricsResult = await getCompanyMetrics({ assesseeOrgId: orgId, dataSource: DATA_SOURCE_TYPE.BOTH }, true);
            // A failed fetch returns null - skip rather than POST back an empty map, which would
            // wholesale-wipe every other companyMetrics section server-side.
            if (!metricsResult) return;
            const companyMetrics = metricsResult.companyMetrics ?? {};
            // Defensive: a flattened borrowerFinancials (server's activeSource marker present) means
            // the nested shape didn't round-trip - bail rather than POST it back and wipe the sibling
            // data source.
            if (companyMetrics.borrowerFinancials?.activeSource) {
                console.warn('Skipping business pincode sync - borrowerFinancials is flattened for org', orgId);
                return;
            }
            companyMetrics.borrowerProfile = {
                ...(companyMetrics.borrowerProfile ?? {}),
                [BUSINESS_DETAILS_FIELD_KEYS.BUSINESS_ADDRESS_PINCODE]: [pincode],
            };
            await updateCompanyMetrics({ orgId, parentOrgId: userData?.investorOrgId, companyMetrics });
        } catch (err) {
            console.error('Error auto-populating business pincode', orgId);
        }
    };

    // Assembles people[]/primaryApplicant/coApplicants and saves via the polus BEFE route.
    // Returns true when Proceed may continue (nothing to save, or the save succeeded); false
    // blocks Proceed (a per-person save error was surfaced via toast).
    const handleSaveMandateApplicants = async (): Promise<boolean> => {
        const flattenedFormData = unflatten(formData);
        const primaryFields = flattenedFormData?.[CONFIG_STEP.primaryApplicant] || {};
        const people = buildMandateApplicantPeople(primaryFields, primaryApplicantLink.managementProfileId, coApplicants);

        const businessDetails = flattenedFormData?.[BUSINESS_DETAILS_KEY] || {};
        const hasBusinessDetails = hasBusinessDetailsData(businessDetails);

        // Nothing on either section -> no POST: a fully empty step is a valid save-nothing Proceed.
        if (!people.length && !hasBusinessDetails) return true;

        // Belt-and-braces: validateForm already gates Proceed on this, but re-check right before
        // the POST in case disablePrimary was bypassed some other way.
        const { totalPresentCount, primaryMarkedCount } = getApplicantPresenceCounts(primaryFields, coApplicants);
        if (totalPresentCount > 0 && primaryMarkedCount !== 1) {
            toast.error(resolveApplicantPrimaryTypeError(totalPresentCount, primaryMarkedCount));
            return false;
        }

        // Which present card is Primary Applicant is decided by its applicantType field, not its
        // physical slot - clientRef stays tied to the physical slot regardless (see
        // splitApplicantCardsByType).
        const { primaryCard, coCards } = splitApplicantCardsByType(primaryFields, primaryApplicantLink, coApplicants);

        setIsSavingMandateApplicants(true);
        const orgId = resolveDrawerOrgId();
        try {
            const response = await saveMandateApplicants({
                dealId: dealDrawerState.dealId,
                orgId,
                changeUserId: getPlatformUserId(),
                people,
                ...(primaryCard
                    ? { primaryApplicant: buildApplicantDetail(primaryCard.fields, primaryCard.clientRef, primaryCard.link.managementProfileId) }
                    : {}),
                coApplicants: coCards.map((card) => buildApplicantDetail(card.fields, card.clientRef, card.link.managementProfileId)),
                ...(hasBusinessDetails ? { businessDetails } : {}),
            });

            const responseBody = response?.data?.responseBody;
            if (shouldBlockOnResponse(responseBody)) {
                const errorMessage = responseBody?.errorMessage;
                const messages = Array.isArray(errorMessage) ? errorMessage : [errorMessage];
                messages.forEach((message: any) =>
                    toast.error(typeof message === 'string' ? message : message?.message || MANDATE_APPLICANT_SAVE_ERROR_PREFIX),
                );
                return false;
            }

            // LINKED/duplicate is not an error - store every returned directorId (joined by
            // clientRef) so a re-opened row becomes returning-edit rather than manual-new. UW's
            // bulkUpsertDirectors returns the ManagementProfile _id under the `directorId` key for
            // every outcome (CREATE/LINK/UPDATE) - it is the stable link identity, so it must be
            // stored as managementProfileId (what resolveApplicantEditState/the link selector
            // read), not the bureau-stamp `directorId` field.
            const savedApplicants: Array<{ clientRef: string; directorId?: string }> = responseBody?.applicants || [];
            savedApplicants.forEach((saved) => {
                if (!saved?.directorId) return;
                if (saved.clientRef === 'primary') {
                    // Merge rather than replace - a fresh save response carries directorId but not
                    // any other primaryApplicantLink state, which must survive this update.
                    setPrimaryApplicantLink((prev) => ({ ...prev, managementProfileId: saved.directorId, linkSource: APPLICANT_LINK_SOURCE.SAVED }));
                    return;
                }
                setCoApplicants((prev) =>
                    prev.map((row) =>
                        row.id === saved.clientRef
                            ? { ...row, managementProfileId: saved.directorId, linkSource: APPLICANT_LINK_SOURCE.SAVED, persisted: true }
                            : row,
                    ),
                );
            });

            // Fire-and-forget: this best-effort sync catches its own errors and never touches
            // component state, so it must not delay Proceed/drawer-close on a slow metrics call.
            syncBusinessPincodeToBorrowerProfile(orgId, businessDetails[BUSINESS_DETAILS_FIELD_KEYS.BUSINESS_ADDRESS_PINCODE]);

            return true;
        } catch (err) {
            // Never log the raw Axios error here - err.config.data is the just-POSTed applicant
            // payload (name/PAN/DOB/phone/addresses), and Sentry's default breadcrumb integration
            // would capture it. Log only a non-PII diagnostic.
            const axiosErr = err as { response?: { status?: number }; message?: string };
            console.error('Error saving mandate applicants', axiosErr?.response?.status ?? axiosErr?.message);
            toast.error(MANDATE_APPLICANT_SAVE_ERROR_PREFIX);
            return false;
        } finally {
            setIsSavingMandateApplicants(false);
        }
    };

    const handlePrimary = async () => {
        if (dealDrawerState?.readonly) return;

        if (source === MANDATE_APPLICATION_FORM) {
            const saved = await handleSaveMandateApplicants();
            if (saved) closeDrawer();
            return;
        }
        if (source === DEBT_PRODUCT_FILTERS) {
            let payload: any = unflatten(formData);
            if (!isEmpty(payload)) payload.filters.filterType = filterType;
            dispatch(setDebtProductsFilters(payload));
            closeDrawer(payload);
            return;
        }
        if (source === 'Debt Products') {
            if (step === 1 && dealId && dealStatus === 'DRAFT' && flowToMandate(formData, formDataRef.current as Record<string, any>)) {
                updateQueryParams(params, setParams, { updateMandate: true }, location);
                return;
            }
            // RECUR-21135: persist Brand Name to OrganisationMaster for the assessee Edit Profile
            // save too — previously only wired below for the logged-in user's own profile edit.
            await persistProfileCustomFields();

            // RECUR-21135: refresh selectedCompany so the report header reflects the just-saved brand
            // without a manual reload (same refetch the Unlock/Refresh flow uses). Resolve the assessee
            // org the same way the save does, so it works whether the id is in the URL or the drawer.
            const refreshedOrgId = resolveDrawerOrgId();
            if (refreshedOrgId) {
                dispatch(
                    fetchSingleCompanyDetails(
                        {
                            assesseeOrgId: refreshedOrgId,
                            investorOrgId: platformData?.investorOrgId,
                        },
                        // The saga calls handlers.onSuccess unconditionally; the reducer already
                        // updates selectedCompany, so a no-op onSuccess is enough here.
                        { onSuccess: () => {} },
                    ),
                );
            }

            // Update metrics, then navigate - await the API call
            await companyMetricsUpdate(() => {
                if (step === formConfig?.length) {
                    closeDrawer();
                } else {
                    navigateStep(step + 1, 'slide-down');
                }
            });
            return;
        } else if (source === PROFILE_BUTTON || source === ORG_USERS_TABLE) {
            // Persist custom fields to OrganisationMaster (separate from metrics)
            await persistProfileCustomFields();

            // Update metrics, then navigate - await the API call
            await companyMetricsUpdate(() => {
                if (step === formConfig?.length) {
                    closeDrawer();
                } else {
                    navigateStep(step + 1, 'slide-down');
                }
            });
            return;
        }

        if (investorData?.dealDrawer?.isPec) {
            // create or update PEC
            createOrUpdatePEC(() => navigateStep(step + 1, 'slide-down'), false);
            return;
        }

        // normal "Create Deal" flow
        const currentConf = formConfig?.[step - 1];
        if (currentConf?.step === CONFIG_STEP.SELECT_INVESTORS) {
            handleCreateDeal();
            return;
        }

        if (step + 1 > formConfig.length) return;
        navigateStep(step + 1, 'slide-down');
    };

    const fetchNegativeLenders = async () => {
        try {
            if (userData?.investorOrgId) {
                const assesseeOrgId =
                    (urlParams.id as string) || user.userData.assesseeOrgId || (dealDrawerState?.companyId as string) || '';
                const response = await fetchAllLendersWithConnections(userData.investorOrgId, assesseeOrgId || undefined);
                const negativeLenderObj = response?.data?.lenderLegalNameIdsMap?.ADMIN;
                if (negativeLenderObj) {
                    let options: any = [];
                    Object.entries(negativeLenderObj).forEach(([lenderName, ids]) => {
                        options.push({
                            label: lenderName,
                            value: ids,
                        });
                    });
                    setNegativeLenderList(options);
                }
                const connectedNames: string[] = response?.data?.connectedLenderNames || [];
                setConnectedLenderNames(connectedNames);
            }
        } catch (error) {
            console.error('Error fetching Negative Lender List:', error);
        }
    };

    const handleSecondary = () => {
        if (source === DEBT_PRODUCT_FILTERS) {
            setFormData({});
            setOriginalFormData({});
            dispatch(setDebtProductsFilters({}));
        }
        if (step <= 1) return;
        navigateStep(step - 1, 'slide-up');
    };

    const handleStepperClick = (index: number) => {
        const direction = index > step ? 'slide-up' : 'slide-down';
        if (investorData?.dealDrawer?.isPec) {
            createOrUpdatePEC(() => navigateStep(index, direction), true);
        } else {
            navigateStep(index, direction);
        }
    };

    const handleUpdateMandate = async () => {
        try {
            await companyMetricsUpdate(() => {});

            let latestMetricsData = {};
            if (fetchCompanyMetrics) {
                latestMetricsData = await fetchCompanyMetrics(false, true);
            }

            if (saveSelection) {
                saveSelection('DRAFT', dealId, '', latestMetricsData);
            }
            continueToNextStep();

            params.delete('updateMandate');
            setParams(params);
        } catch (e) {
            console.error('Update failed', e);
        }
    };
    const fetchCitiesList = async () => {
        try {
            if (!userData?.investorOrgId) {
                return;
            }

            const response = await fetchDataByConfig(
                {
                    parentOrgId: userData.investorOrgId,
                },
                'GET_ALL_CITIES',
                'GET',
                polusBaseUrl,
            );

            const citiesArr = response?.data?.responseBody?.AICA_GET_CITIES_LIST?.cities;
            if (Array.isArray(citiesArr)) {
                const cityOptions = citiesArr.map((city) => ({ label: city, value: city }));

                SetCitiesList(cityOptions);
            } else {
                SetCitiesList([]);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            SetCitiesList([]);
        }
    };

    const handleSkipMandate = async () => {
        try {
            await companyMetricsUpdate(() => continueToNextStep());
            params.delete('updateMandate');
            setParams(params);
        } catch (e) {
            console.error('Skip failed', e);
        }
    };

    // 7. Create or Update "PEC"
    const createOrUpdatePEC = (navigationFunction: () => void, isStepper: boolean) => {
        setLoadingPrefilledData(true);

        const actualUser = JSON.parse(localStorage.getItem('user_data') ?? '{}')?.userName;

        // Important identifiers that should always be included
        const basePayload = {
            aggregatorId: platformData?.investorOrgId,
            lenderId: userOrgId,
            activityBy: actualUser ?? loggedInUser?.userName,
            type:
                userData?.accountType === USER_TYPE.INVESTOR
                    ? USER_TYPE.ADMIN
                    : isLenderType(userData?.accountType)
                    ? USER_TYPE.LENDER
                    : userData?.accountType,
            status: isFromComplete ? 'COMPLETE' : 'PENDING',
        };

        const isUpdate =
            (investmentPreferenceId && !params.has('Duplicate')) ||
            ((source === PROFILE_BUTTON || source === ORG_USERS_TABLE) && formData?.id);

        // Get loan types regardless of changes since it's required
        const unflattened = unflatten(formData) as { preferredTerms?: { loanTypes?: string[] } };
        const currentProducts = unflattened?.preferredTerms?.loanTypes
            ?.map((productName: string) => loanTypeNameToIdMap.get(productName))
            ?.filter(Boolean);

        if (!currentProducts?.length) {
            setLoadingPrefilledData(false);
            // For create/Duplicate flow: allow navigation to next step when we're before Preferred Terms
            // (user needs to reach that step to select loan types). Only require products when we're on or past that step.
            const preferredTermsStepIndex = formConfig?.findIndex((c: any) => c?.step === CONFIG_STEP.PREFERRED_TERMS);
            const isBeforePreferredTermsStep = preferredTermsStepIndex >= 0 && step - 1 < preferredTermsStepIndex;
            if (!isUpdate && isBeforePreferredTermsStep) {
                navigationFunction();
                return;
            }
            toast.error('Please select at least one loan type');
            return;
        }

        if (isUpdate) {
            // Get changed data by comparing with initial form data
            const changedData: { [key: string]: any } = {};
            // ➊ Updated / added keys
            Object.keys(formData).forEach((key) => {
                if (!isEqual(formData[key], initialFormData[key])) {
                    changedData[key] = formData[key];
                }
            });

            // ➋ Deleted keys
            Object.keys(initialFormData).forEach((key) => {
                if (!(key in formData)) {
                    changedData[key] = null; // or `undefined` depending on API contract
                }
            });

            // If no changes and not a stepper navigation, just navigate
            if (Object.keys(changedData).length === 0) {
                setLoadingPrefilledData(false);
                if (!isStepper && formConfig?.[step - 1]?.ctas?.primary?.text === 'Save & Close') {
                    closeDrawer();
                    return;
                }
                navigationFunction();
                return;
            }

            // Create payload with only changed data while preserving structure
            const payload = {
                ...basePayload,
                investmentPreferenceId,
                products: currentProducts, // Always include products
                ...unflatten(changedData, { includeNull: true }), // Include only changed data
                isActive: formData?.isActive,
            };

            Mixpanel.track('PEC Edited', {
                userEmail: userData?.primaryEmail,
                PNOId: userData?.investorOrgId,
                PECId: investmentPreferenceId,
                LoanTypes: payload.products,
                MetricsUpdated: changedData, // Track only changed fields
                UpdatedBy: userData?.userName,
                LastUpdated: new Date().toISOString(),
            });

            dispatch(
                updateInvestmentPreference(payload, {
                    onSuccess: () => {
                        setLoadingPrefilledData(false);
                        setInitialFormData(formData); // Update initial form data after successful update
                        setOriginalFormData(formData);
                        if (!isStepper && formConfig?.[step - 1]?.ctas?.primary?.text === 'Save & Close') {
                            closeDrawer();
                            const message = getToastMessages(source);
                            toast.success(message.success);
                            return;
                        }
                        navigationFunction();
                    },
                    onError: () => {
                        setLoadingPrefilledData(false);
                        const message = getToastMessages(source);
                        toast.error(message.error);
                    },
                }),
            );
        } else {
            // For new creation, include all data
            // Snapshot what is actually sent: the create response arrives in a closure that must
            // not read a later formData.
            const sentFormData = { ...formData };
            const payload = {
                ...basePayload,
                ...unflatten(formData, { includeNull: true }),
                products: currentProducts,
                investmentPreferenceType: source === PROFILE_BUTTON || source === ORG_USERS_TABLE ? PROFILE : PEC_STATUS.PEC,
                isActive: formData?.isActive,
            };

            // Track appropriate event
            if (params.has('Duplicate')) {
                Mixpanel.track(PEC_STATUS.PEC_DUPLICATED, {
                    userEmail: userData?.primaryEmail,
                    PNOId: userData?.investorOrgId,
                    LoanTypes: payload.products,
                    DuplicatedBy: userData?.userName,
                    DuplicationTime: new Date().toISOString(),
                });
            } else {
                Mixpanel.track(PEC_STATUS.PEC_CREATED, {
                    userEmail: userData?.primaryEmail,
                    PNOId: userData?.investorOrgId,
                    PNOName: subDomain,
                    LoanTypes: payload.products,
                    CreationTime: new Date().toISOString(),
                });
            }

            dispatch(
                createInvestmentPreference(payload, {
                    onSuccess: (res) => {
                        // Without a baseline, a removal later in the same session is undetectable —
                        // deletions are computed against initialFormData.
                        setInitialFormData(sentFormData);
                        setOriginalFormData(sentFormData);
                        if (source === PROFILE_BUTTON || source === ORG_USERS_TABLE) {
                            setLoadingPrefilledData(false);
                            closeDrawer();
                            const messages = getToastMessages(source);
                            toast.success(messages.success);
                            return;
                        }
                        params.set('investmentPreferenceId', res?.investmentPreferenceId);
                        setParams(params);
                        if (params.has('Duplicate')) {
                            params.delete('Duplicate');
                            updateQueryParams(params, setParams, {});
                        }
                        setInvestmentPreferenceId(res?.investmentPreferenceId);
                        navigateStep(step + 1, 'slide-down');
                        setLoadingPrefilledData(false);
                    },
                    onError: () => {
                        setLoadingPrefilledData(false);
                        const messages = getToastMessages(CREATE_INVESTMENT_PREFERENCE);
                        toast.error(messages.error);
                    },
                }),
            );
        }
    };

    // Resolves the org this Debt Products / Edit Profile drawer instance is acting on — shared by
    // companyMetricsUpdate (financial metrics) and persistProfileCustomFields (Brand Name) so a
    // single save always writes both to the same org.
    const resolveDrawerOrgId = (): string | undefined => {
        if (urlParams.id) return urlParams.id;
        if (params.get('orgId')) return params.get('orgId') as string;
        if (dealDrawerState.companyId) return dealDrawerState.companyId;
        if (isLender) return userData?.lenderOrgId as string;
        if (userData?.accountType === 'ASSESSEE') return userData?.assesseeOrgId;
        if (isPartner) return userOrgId;
        return userData?.investorOrgId;
    };

    // 8a. Persist profile custom fields (+ Brand Name) to OrganisationMaster
    const persistProfileCustomFields = (): Promise<void> => {
        const unflattened: any = unflatten(formData);
        const isOwnProfileFlow = source === PROFILE_BUTTON || source === ORG_USERS_TABLE;

        // RECUR-21135: the logged-in user's own profile edit (PROFILE_BUTTON/ORG_USERS_TABLE)
        // targets the caller's own org; the assessee "Edit Profile" (Debt Products) must target the
        // assessee org in the URL instead — reuse companyMetricsUpdate's org resolution so both
        // writes in the same save land on the same org.
        const orgId = isOwnProfileFlow
            ? params.get('orgId') || (isPartner && userOrgId) || (isLender ? userData?.lenderOrgId : userData?.investorOrgId) || ''
            : // resolveDrawerOrgId can return undefined — mirror the same `|| companyId` guard the
              // metrics path (companyMetricsUpdate) uses so an undefined id is never sent as orgId.
              resolveDrawerOrgId() || (dealDrawerState.companyId as string);

        // PROFILE_CUSTOM_FIELD_KEYS (priority, coi, sectoralPreference, ...) belong to the
        // logged-in user's own "profile" section (AICA_YOUR_PROFILE/AICA_PARTNER_PROFILE) — the
        // borrower Edit Profile config (AICA_COMPANY_PROFILE) has no such section, so skip them
        // here rather than writing 8 unrelated empty-string fields onto the assessee's org.
        let customFieldsPayload: Record<string, any> | undefined;
        if (isOwnProfileFlow) {
            const profileData = unflattened?.profile || {};
            const payload: Record<string, any> = {};
            // Always include all keys so cleared fields (deleted by handleChange) also reach the server
            PROFILE_CUSTOM_FIELD_KEYS.forEach((key) => {
                payload[key] = profileData[key] !== undefined ? profileData[key] : '';
            });
            customFieldsPayload = payload;
        }

        // RECUR-21135 R4: Brand Name (AICA_COMPANY_PROFILE's borrowerProfile.displayOrgName) is an
        // OrganisationMaster field, not a customField — updateOrgMaster only persists it from
        // orgInfo.brandName (see AccountServiceImpl#updateOrgMaster). Sending it as a customField
        // would silently land in OrganisationMaster.customFields.displayOrgName instead of the real
        // top-level displayOrgName field the brand resolver reads, so it's split out here. Registered
        // Name (borrowerProfile.orgName) is read-only and is never sent — R5 guarantees the server
        // never mutates orgName from this endpoint either way.
        const brandName = unflattened?.borrowerProfile?.displayOrgName;
        const orgInfo = typeof brandName === 'string' && brandName.trim() ? { brandName } : undefined;

        return new Promise<void>((resolve, reject) => {
            dispatch(
                updateCompany(
                    { orgId, ...(customFieldsPayload ? { customFields: customFieldsPayload } : {}), ...(orgInfo ? { orgInfo } : {}) },
                    { onSuccess: resolve, onError: reject },
                ),
            );
        });
    };

    // 8b. Update "Company Metrics" (Debt Products & capital requirements flow)
    const companyMetricsUpdate = async (navigationFunction: () => void) => {
        Mixpanel.track('Profile Updated', {
            PNOId: userData?.investorOrgId,
            userEmail: userData?.primaryEmail,
        });

        const orgId = resolveDrawerOrgId();

        try {
            const metrics: any = unflatten(formData);
            const financials = metrics?.borrowerFinancials || {};

            const hasActualData = hasFinancialDataInNestedObject(financials.actualData);
            const hasDiscussionData = hasFinancialDataInNestedObject(financials.discussion);
            const hasAnyFinancialData = hasActualData || hasDiscussionData;

            if (hasAnyFinancialData && !financials.dataSource) {
                toast.error('Please select a Data Source for this entry');
                return;
            }

            // Remove entire borrowerFinancials object if no input data exists
            if (!hasAnyFinancialData && metrics.borrowerFinancials) delete metrics.borrowerFinancials;

            // For profile drawer, customFields are saved separately to OrganisationMaster via updateCompany
            if (source === PROFILE_BUTTON || source === ORG_USERS_TABLE) {
                delete metrics.customFields;
                if (metrics.profile) {
                    PROFILE_CUSTOM_FIELD_KEYS.forEach((key) => {
                        delete metrics.profile[key];
                    });
                }
            }
            // RECUR-21135: Brand Name (displayOrgName) is persisted via persistProfileCustomFields ->
            // updateOrgMaster (orgInfo.brandName) above for both the logged-in user's own profile edit
            // and the assessee Edit Profile (Debt Products) — strip it here so a stale/duplicate copy
            // isn't also written to OrganizationAdditionalDetail (nothing reads the brand from there).
            // Registered Name (orgName) is read-only and must never be persisted anywhere from this
            // form, so it is stripped alongside it.
            if ((source === PROFILE_BUTTON || source === ORG_USERS_TABLE || source === 'Debt Products') && metrics.borrowerProfile) {
                delete metrics.borrowerProfile.displayOrgName;
                delete metrics.borrowerProfile.orgName;
            }

            await updateCompanyMetrics({
                orgId: orgId || (dealDrawerState.companyId as string),
                parentOrgId: userData?.investorOrgId,
                companyMetrics: metrics,
            });

            navigationFunction?.();
        } catch (err) {
            console.error(err);
        }
    };

    // 9. Create Deal
    const handleCreateDeal = () => {
        const payload: any = unflatten(formData);
        payload.dealProposerId = platformData?.investorOrgId;
        payload.dealBeneficiaryId = investorData?.dealDrawer?.companyId;
        payload.dealCreatedBy = loggedInUser?.userName;
        payload.owner = {
            name: loggedInUser?.userName,
            userId: getPlatformUserId(),
            activityBy: loggedInUser?.userName,
            activityDate: new Date().toISOString(),
            ownerType: DEFAULT_OWNER_TYPE,
        };
        if (payload?.dealTerms?.dealNotes) {
            payload.dealTerms.dealNotes = [
                {
                    note: payload.dealTerms.dealNotes,
                    noteBy: loggedInUser?.userName,
                    noteDate: new Date().toISOString(),
                },
            ];
        }
        dispatch(
            createDeal(payload, {
                onSuccess: () => {
                    closeDrawer();
                    navigate('/investor/app/deals', { state: { scrollPosition: 0 } });
                },
            }),
        );
    };

    // 10. Reset local storage
    const resetEditorDataInLocalStorage = () => {
        localStorage.removeItem('one_pager_content');
        localStorage.removeItem('one_pager_assessee');
    };

    // 11. Get toast messages,error messages, getInputValidation
    const getToastMessages = (source: string) => {
        switch (source) {
            case ORG_USERS_TABLE:
            case PROFILE_BUTTON:
                return {
                    success: 'Profile Details saved successfully',
                    error: 'Failed to update profile details',
                };
            case CREATE_INVESTMENT_PREFERENCE:
                return {
                    success: 'Investment Preferences created successfully',
                    error: 'Failed to create investment preference',
                };
            default:
                return {
                    success: 'Investment Preferences saved successfully',
                    error: 'Failed to update investment preference',
                };
        }
    };

    const getInputValidation = (value: string, key: string) => {
        switch (key) {
            case BORROWER_PROFILE_CONSTANTS.WEBSITE:
                return isWebsiteValid;
            case BORROWER_PROFILE_CONSTANTS.LINKEDIN:
                return isLinkedInValid || !value;
            default:
                return true;
        }
    };

    // -------------------------------------
    // HOOKS
    // -------------------------------------

    useWindowUnloadEffect(() => {
        resetEditorDataInLocalStorage();
    }, true);

    useEffect(() => {
        return () => {
            resetEditorDataInLocalStorage();
        };
    }, []);

    useEffect(() => {
        createMapOfRequiredVsSectionKeys();
    }, [formConfig]);

    useEffect(() => {
        checkCompleteForm();
    }, [formData, restrictedMap]);

    // Keep a ref to latest formData for use in the initialization timer
    const latestFormDataRef = useRef(formData);
    useEffect(() => {
        latestFormDataRef.current = formData;
    }, [formData]);

    // Mark form as initialized after data load is complete and a brief delay
    // This allows InputRenderer to set default values before we start tracking changes.
    // Note: 500ms delay accounts for React rendering cycle + InputRenderer useEffect execution.
    // This is a pragmatic approach; a more deterministic solution would require InputRenderer
    // to signal when all defaults are applied, but that adds significant complexity.
    useEffect(() => {
        // Don't initialize until data loading is complete and form config is available
        if (loadingPrefilledData || !formConfig || isFormInitialized) return;

        // Wait for InputRenderer default values to be applied, then snapshot the current formData
        const timer = setTimeout(() => {
            // Replace originalFormData with the current state after defaults are applied.
            // This ensures both server-loaded data AND default values are captured as the baseline.
            const currentFormData = latestFormDataRef.current;
            setOriginalFormData({ ...currentFormData });
            setIsFormInitialized(true);
        }, 500);
        return () => clearTimeout(timer);
    }, [loadingPrefilledData, formConfig, isFormInitialized]);

    useEffect(() => {
        // Don't track changes until form is fully initialized
        // This prevents false positives from default values being set by InputRenderer
        if (!isFormInitialized) {
            setHasUnsavedChanges(false);
            return;
        }

        // Normalize objects before comparison by removing empty/null/undefined values
        // This is needed because handleChange deletes keys with empty values,
        // but originalFormData may have those keys with empty string values
        const normalizeForComparison = (obj: Record<string, any>) => {
            const normalized: Record<string, any> = {};
            Object.entries(obj).forEach(([key, value]) => {
                // Only include non-empty values (treat empty string, null, undefined as "no value")
                if (value !== '' && value !== null && value !== undefined) {
                    normalized[key] = value;
                }
            });
            return normalized;
        };

        const normalizedFormData = normalizeForComparison(formData);
        const normalizedOriginalData = normalizeForComparison(originalFormData);
        const hasChanges = !isEqual(normalizedFormData, normalizedOriginalData);
        setHasUnsavedChanges(hasChanges);
    }, [formData, originalFormData, isFormInitialized]);

    useEffect(() => {
        validateForm();
        // primaryApplicantLink is a dep because the duplicate-applicant + one-Primary gates read it;
        // without it a link change that doesn't also touch formData wouldn't re-run the gate.
    }, [step, formData, formConfig, coApplicants, isSavingMandateApplicants, primaryApplicantLink]);

    // Check for "Duplicate" param
    useEffect(() => {
        if (params.has('Duplicate')) {
            setDuplicate(true);
        }
    }, [params]);

    useEffect(() => {
        if (source === DEBT_PRODUCT_FILTERS) {
            setDisableSecondary(isEmpty(formData));
        } else {
            setDisableSecondary(step === 0 || !isWebsiteValid || !isLinkedInValid);
        }
    }, [formData, source, step]);

    useEffect(() => {
        if (duplicate) {
            params.delete('investmentPreferenceId');
            updateQueryParams(params, setParams, {});
        }
    }, [duplicate, params, setParams]);

    // Load the form config
    useEffect(() => {
        if (source === DEBT_PRODUCT_FILTERS) {
            setFilterType(investorData?.aicaDebtProductConfig?.filters?.filterType ?? '');
            setFormConfig(investorData?.aicaDebtProductConfig?.filters?.pages ?? []);
            setStepperConfig([]);
            return;
        }
        if (!platformData || !loggedInUser) return;
        const configName = investorData?.dealDrawer?.isPec
            ? AICA_INVESTMENT_PREFERENCE
            : source === 'Debt Products' || source === MANDATE_APPLICATION_FORM
            ? AICA_COMPANY_PROFILE
            : source === PROFILE_BUTTON || source === ORG_USERS_TABLE
            ? investorData?.dealDrawer?.userType === USER_TYPE.PARTNER
                ? AICA_PARTNER_PROFILE
                : AICA_YOUR_PROFILE
            : AICA_CREATE_DEAL_DETAILS;
        getDealFormConfig(
            {
                accountType: investorData?.dealDrawer?.isLenderOrg ? USER_TYPES.LENDER : loggedInUser.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: loggedInUser?.primaryEmail,
                configName,
            },
            {
                onSuccess: (configData, orgName, displayOrgName) => {
                    if (orgName) setInvestorName(orgName);
                    let pages = configData?.pages ?? [];
                    let stepper = configData?.stepper ?? [];
                    if (configName === AICA_CREATE_DEAL_DETAILS && location.pathname.includes('/deals')) {
                        pages = pages.filter((page: any) => page.step !== CONFIG_STEP.SELECT_INVESTORS);
                        stepper = stepper.filter((item: any) => item.stepId !== CONFIG_STEP.SELECT_INVESTORS);
                    }
                    // The Application Form drawer renders ONLY the Primary Applicant step -
                    // same technique the /deals filter above uses for SELECT_INVESTORS.
                    if (source === MANDATE_APPLICATION_FORM) {
                        pages = pages.filter((page: any) => page.step === CONFIG_STEP.PRIMARY_APPLICANT);
                        stepper = stepper.filter((item: any) => item.stepId === CONFIG_STEP.PRIMARY_APPLICANT);
                    }
                    setFormConfig(pages);
                    setStepperConfig(stepper);
                    setDisplayOrgName(displayOrgName);
                },
            },
        );
        fetchCitiesList();
    }, [platformData, loggedInUser, investorData?.dealDrawer?.isPec, source]);

    // Fetch data, prefill, etc. (examples)
    // Example: if there's a dealId, getDealData, etc.
    useEffect(() => {
        if (!investorData?.dealDrawer?.dealId || investorData?.dealDrawer?.isPec) return;
        dispatch(
            getDealData(
                { dealId: investorData?.dealDrawer?.dealId },
                {
                    onSuccess: (dealData) => {
                        const dealNotes = dealData?.dealTerms?.dealNotes?.[0]?.note;
                        if (dealNotes) {
                            dealData.dealTerms.dealNotes = dealNotes;
                        }
                        const flattenedData = flatten(dealData);

                        // The applicant roster + Business Details are persisted under
                        // Deal.customFields, not the top level - flatten(dealData) alone can't
                        // seed the primaryApplicant.*/businessDetails.* keys ApplicantFieldsBlock/
                        // BusinessDetailsSection read, so merge them in explicitly.
                        const savedPrimaryApplicant = dealData?.customFields?.[CONFIG_STEP.primaryApplicant] || {};
                        const savedBusinessDetails = dealData?.customFields?.[BUSINESS_DETAILS_KEY] || {};
                        // The Deal detail round-trips managementProfileId (the stable link
                        // identity) - key rehydration on it so a saved link is recognised as
                        // RETURNING_EDIT even when the bureau directorId is absent (see
                        // coApplicantTypes.resolveApplicantEditState). Fall back to directorId
                        // for rows saved before managementProfileId was persisted.
                        const savedPrimaryLinkId = resolveSavedApplicantLinkId(savedPrimaryApplicant);
                        const isSavedPrimaryPresent = isApplicantEntryPresent(savedPrimaryApplicant) || !!savedPrimaryLinkId;
                        const mergedFormData =
                            source === MANDATE_APPLICATION_FORM
                                ? {
                                      ...flattenedData,
                                      ...flatten(savedPrimaryApplicant, CONFIG_STEP.primaryApplicant),
                                      // Role is decided by the bucket the applicant was saved in, not stored
                                      // on the saved object itself - seed it explicitly on rehydration.
                                      ...(isSavedPrimaryPresent
                                          ? { [`${CONFIG_STEP.primaryApplicant}.${MANDATE_APPLICANT_TYPE_KEY}`]: MANDATE_APPLICANT_DESIGNATION_LABELS.PRIMARY }
                                          : {}),
                                      // Re-derive the toggle from the saved addresses - it is never
                                      // itself persisted, so flatten(savedPrimaryApplicant) above
                                      // never produces this key.
                                      [`${CONFIG_STEP.primaryApplicant}.${MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_SAME_AS_CURRENT}`]:
                                          resolveSavedPermanentSameAsCurrent(savedPrimaryApplicant),
                                      ...flatten(savedBusinessDetails, BUSINESS_DETAILS_KEY),
                                  }
                                : flattenedData;
                        setFormData(mergedFormData);
                        setOriginalFormData(mergedFormData);

                        if (source === MANDATE_APPLICATION_FORM) {
                            const savedPrimaryDirectorId = savedPrimaryApplicant?.directorId;
                            setPrimaryApplicantLink(
                                savedPrimaryLinkId
                                    ? {
                                          // savedPrimaryLinkId's directorId fallback decides presence/RETURNING_EDIT
                                          // only - never populate managementProfileId with a bureau directorId.
                                          directorId: savedPrimaryDirectorId || null,
                                          managementProfileId: savedPrimaryApplicant?.managementProfileId || null,
                                          linkSource: APPLICANT_LINK_SOURCE.SAVED,
                                      }
                                    : {},
                            );
                            setPrimarySlotActive(isSavedPrimaryPresent);
                            const savedCoApplicants = Array.isArray(dealData?.customFields?.applicants)
                                ? dealData.customFields.applicants
                                : [];
                            setCoApplicants(
                                savedCoApplicants.map((coApplicant: Record<string, any>, index: number) => {
                                    // linkId's directorId fallback decides linkSource/RETURNING_EDIT only - the
                                    // spread below already carries the row's real managementProfileId/directorId.
                                    const linkId = resolveSavedApplicantLinkId(coApplicant);
                                    return {
                                        ...coApplicant,
                                        id: `savedCoApplicant-${index}`,
                                        persisted: true,
                                        linkSource: linkId ? APPLICANT_LINK_SOURCE.SAVED : undefined,
                                        [MANDATE_APPLICANT_TYPE_KEY]: MANDATE_APPLICANT_DESIGNATION_LABELS.CO_APPLICANT_LABEL,
                                        // Re-derive the toggle from the saved addresses - see the
                                        // primary applicant seeding above for why.
                                        [MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_SAME_AS_CURRENT]: resolveSavedPermanentSameAsCurrent(coApplicant),
                                    };
                                }),
                            );
                        }
                    },
                },
            ),
        );
    }, [investorData?.dealDrawer.dealId, investorData?.dealDrawer.isPec, source, dispatch]);

    // Existing-director options for the Application Form drawer's "Select Applicants" picker.
    // Keyed on managementProfileId (always present) rather than directorId (the bureau stamp,
    // often absent) - a profile with no directorId is still linkable, just without a bureau link.
    useEffect(() => {
        if (source !== MANDATE_APPLICATION_FORM) return;
        const orgId = resolveDrawerOrgId();
        if (!orgId) return;
        fetchManagementProfilesV2({ orgId }).then((res: any) => {
            const profiles = res?.managementProfile?.profiles || [];
            setExistingDirectors(
                profiles.map((profile: any) => ({
                    managementProfileId: profile.managementProfileId,
                    directorId: profile.directorId || undefined,
                    name: profile.name,
                    pan: profile.pan,
                    phone: profile.phone,
                    email: profile.email,
                })),
            );
        });
        // resolveDrawerOrgId also reads urlParams/userData/role flags, but those are fixed for the
        // drawer's lifetime - only companyId varies as the user switches mandates, so it is the sole
        // reactive input the picker needs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, dealDrawerState.companyId]);

    useEffect(() => {
        if (!investorData?.dealDrawer?.lenderDealId) return;
        dispatch(
            getLenderDealData(
                { dealId: investorData?.dealDrawer?.lenderDealId },
                {
                    onSuccess: (dealData) => {
                        const dealNotes = dealData?.dealTerms?.dealNotes?.[0]?.note;
                        if (dealNotes) {
                            dealData.dealTerms.dealNotes = dealNotes;
                        }
                        const flattenedData = flatten(dealData);
                        setFormData(flattenedData);
                        setOriginalFormData(flattenedData);
                    },
                },
            ),
        );
    }, [investorData?.dealDrawer.lenderDealId, dispatch]);

    // If an existing PEC ID is in the query param
    useEffect(() => {
        if (!investorData?.dealDrawer?.isPec && source !== PROFILE_BUTTON && source !== ORG_USERS_TABLE) return;

        const prefId = investmentPreferenceId || params.get('investmentPreferenceId') || '';
        if (!prefId && source !== PROFILE_BUTTON && source !== ORG_USERS_TABLE) return;

        setInvestmentPreferenceId(prefId);

        // For regular PEC flow
        if (prefId && source !== PROFILE_BUTTON && source !== ORG_USERS_TABLE) {
            dispatch(
                getInvestmentPreference(
                    { investmentPreferenceId: prefId },
                    {
                        onSuccess: (data: any) => {
                            if (params.has('Duplicate')) {
                                data.preferredTerms = { ...data.preferredTerms, loanTypes: [] };
                            }
                            const flattenedData = flatten(data);
                            setFormData(flattenedData);
                            setInitialFormData(flattenedData);
                            setOriginalFormData(flattenedData);
                        },
                    },
                ),
            );
            return;
        } else if ((source === PROFILE_BUTTON || source === ORG_USERS_TABLE) && !loadingPrefilledData) {
            const assesseeOrgId =
                params.get('orgId') || (isPartner && userOrgId) || (isLender ? userData?.lenderOrgId : userData?.investorOrgId) || '';
            setLoadingPrefilledData(true);

            Promise.all([getCompanyMetrics({ assesseeOrgId }), fetchOrgInfoById({ orgId: assesseeOrgId })])
                .then(([metricsRes, orgInfo]) => {
                    const metricsData = metricsRes?.companyMetrics || metricsRes || {};
                    metricsData.profile = metricsData.profile || {};
                    metricsData.profile.organisationName = orgInfo?.orgName ?? '';
                    metricsData.profile.organisationDisplayName = orgInfo?.orgDisplayName ?? '';
                    metricsData.profile.feeShare = orgInfo?.customFields?.partnerFee ?? '';

                    // RECUR-21135 R4/R5: prefill the borrower Edit Profile form's Brand Name
                    // (editable) and Registered Name (read-only) from the freshly-fetched org info —
                    // these live on OrganisationMaster, not in the metrics blob, so nothing else
                    // populates borrowerProfile.displayOrgName/orgName and they'd otherwise render blank.
                    metricsData.borrowerProfile = metricsData.borrowerProfile || {};
                    applyBrandPrefill(metricsData.borrowerProfile, orgInfo);

                    // Map customFields from OrganisationMaster into profile section for the drawer
                    const orgCustomFields = orgInfo?.customFields || {};
                    PROFILE_CUSTOM_FIELD_KEYS.forEach((key) => {
                        if (orgCustomFields[key] !== undefined) {
                            metricsData.profile[key] = orgCustomFields[key];
                        }
                    });

                    if (source === PROFILE_BUTTON && (isLenderType(userData?.accountType) || isPartner)) {
                        setInvestorName(orgInfo?.orgDisplayName ? orgInfo?.orgDisplayName : orgInfo?.orgName);
                    }

                    const flattenedData = flatten(metricsData);
                    setFormData(flattenedData);
                    setInitialFormData(flattenedData);
                    setOriginalFormData(flattenedData);
                    setLoadingPrefilledData(false);
                })
                .catch((error) => {
                    console.error('Error fetching data:', error);
                    setLoadingPrefilledData(false);
                });
        }
    }, [investorData?.dealDrawer?.isPec, params, dispatch, source, userData, isLender, userOrgId]);

    useEffect(() => {
        if (
            (isLenderType(userData?.accountType) || isPartner) &&
            ([PROFILE_BUTTON].includes(source) || investorData?.dealDrawer?.isPec) &&
            displayOrgName
        ) {
            setInvestorName(displayOrgName);
        }
    }, [displayOrgName, source, userData?.accountType]);

    const fetchLoanTypes = async () => {
        const isDebtProducts = source === 'Debt Products' || formConfig?.[step - 1]?.step === CONFIG_STEP.DEAL_TERMS;
        let payload: any = {
            aggregatorId: userData?.investorOrgId,
        };
        if (investmentPreferenceId && !params.has('Duplicate')) {
            payload.investmentPreferenceId = investmentPreferenceId;
        }
        if (isLender) {
            payload.lenderId = userData?.lenderOrgId;
        }
        try {
            const response = await fetchAllAvailableProducts(payload, isDebtProducts);
            if (response) {
                setLoanTypeOptions(response);
            }
        } catch (error) {
            console.error('Error fetching loan types:', error);
        }
    };

    // LoanTypes, Sectors, etc.
    useEffect(() => {
        if (
            (investorData?.dealDrawer?.isPec && formConfig?.[step - 1]?.step === CONFIG_STEP.PREFERRED_TERMS) ||
            source === 'Debt Products' ||
            formConfig?.[step - 1]?.step === CONFIG_STEP.DEAL_TERMS
        ) {
            fetchLoanTypes();
            fetchNegativeLenders();
        }
    }, [formConfig?.[step - 1]]);

    // Build the name->id map once we have loanTypeOptions
    useEffect(() => {
        if (loanTypeOptions) {
            const productMap = new Map<string, string>();
            Object.values(loanTypeOptions).forEach((product: any) => {
                productMap.set(product.productName, product.productId);
            });
            setLoanTypeNameToIdMap(productMap);
        }
    }, [loanTypeOptions]);

    // Example: fetch Sectors
    useEffect(() => {
        const fetchCompanySector = async () => {
            try {
                const response = await fetchCompanySectorsList();
                if (response) {
                    setSectorList(response?.[AICA_GET_COMPANY_SECTOR_LIST]?.data?.positiveSector ?? []);
                    setNegativeSectorList(response?.[AICA_GET_COMPANY_SECTOR_LIST]?.data?.negativeSector ?? []);
                }
            } catch (error) {
                console.error('Error fetching company sectors:', error);
            }
        };
        const currentStep = formConfig?.[step - 1]?.step;
        const shouldFetch =
            currentStep === CONFIG_STEP.BORROWER_PROFILE ||
            currentStep === CONFIG_STEP.DEAL_TERMS ||
            source === PROFILE_BUTTON ||
            source === ORG_USERS_TABLE;
        if (!shouldFetch) return;
        fetchCompanySector();
    }, [formConfig, step, source]);

    useEffect(() => {
        // The Application Form drawer has no website field - its Proceed gate is computed
        // entirely by validateForm above and must not be overwritten here.
        if (source === MANDATE_APPLICATION_FORM) return;
        if (!formData[BORROWER_PROFILE_CONSTANTS.WEBSITE]) {
            setIsWebsiteValid(true);
            setDisablePrimary(!isLinkedInValid);
            setDisableSecondary(!isLinkedInValid);
        }
    }, [formData, source]);

    const validateLinkedIn = (linkedin, isWebsiteValid) => {
        if (!linkedin) return { isValid: true, shouldDisable: !isWebsiteValid };

        const isValid = linkedInRegex.test(linkedin);

        return { isValid, shouldDisable: !isValid || !isWebsiteValid };
    };

    useEffect(() => {
        // The Application Form drawer has no LinkedIn field - its Proceed gate is computed
        // entirely by validateForm above and must not be overwritten here.
        if (source === MANDATE_APPLICATION_FORM) return;
        const linkedin = formData[BORROWER_PROFILE_CONSTANTS.LINKEDIN];
        const { isValid, shouldDisable } = validateLinkedIn(linkedin, isWebsiteValid);

        setIsLinkedInValid(isValid);
        setDisablePrimary(shouldDisable);
        setDisableSecondary(shouldDisable);
    }, [formData, isWebsiteValid, source]);

    // Watch for changes to borrowerFinancials data source in formData and sync with state
    // Support both dataSource (from config) and activeSource (from data API)
    useEffect(() => {
        const activeSourceValue = formData['borrowerFinancials.dataSource'];
        if (
            activeSourceValue &&
            (activeSourceValue === DATA_SOURCE_TYPE.ACTUAL_DATA || activeSourceValue === DATA_SOURCE_TYPE.DISCUSSION)
        ) {
            if (activeSourceValue !== activeFinancialsTab) {
                setActiveFinancialsTab(activeSourceValue);
            }
        }
    }, [formData['borrowerFinancials.dataSource']]);

    // Example: fetch default metrics if it's not PEC flow
    useEffect(() => {
        if (!platformData || !formConfig || investorData?.dealDrawer?.isPec) return;

        // If source = "Debt Products Filters"
        if (source === DEBT_PRODUCT_FILTERS) {
            // make a deep copy of debtProductFilters
            let payload = JSON.parse(JSON.stringify(debtProductFilters ?? {}));
            // remove filterType from payload.filters
            if (payload?.filters?.filterType) delete payload?.filters?.filterType;
            const flattenedData = flatten(payload);
            setFormData(flattenedData);
            setOriginalFormData(flattenedData);
            return;
        }
        // If source = "Debt Products"
        if (source === 'Debt Products' && !preFilledDataFetched) {
            setLoadingPrefilledData(true);
            const assesseeOrgId = (urlParams.id as string) || user.userData.assesseeOrgId || (dealDrawerState.companyId as string);
            Promise.all([
                getCompanyMetrics({
                    assesseeOrgId,
                    dataSource: DATA_SOURCE_TYPE.BOTH,
                }),
                fetchOrgInfoById({ orgId: assesseeOrgId }),
            ])
                .then(([response, orgInfo]) => {
                    const data = response?.companyMetrics || response || {};
                    const borrowerProfile = data?.borrowerProfile ?? {};
                    // RECUR-21135 R4/R5: prefill the assessee Edit Profile form's Brand Name
                    // (editable) and Registered Name (read-only) from the freshly-fetched org info —
                    // mirrors the PROFILE_BUTTON/ORG_USERS_TABLE prefill above so the two entry
                    // points for this same OrganisationMaster data can't drift apart again.
                    applyBrandPrefill(borrowerProfile, orgInfo);
                    const borrowerFinancials = data?.borrowerFinancials ?? {};

                    // Handle both dataSource (config) and activeSource (data API)
                    if (borrowerFinancials?.dataSource || borrowerFinancials?.activeSource) {
                        setActiveFinancialsTab(borrowerFinancials.dataSource || borrowerFinancials.activeSource);
                    }

                    if (borrowerProfile.website) {
                        setIsWebsitePrefilled(true);
                    } else {
                        setIsWebsitePrefilled(false);
                    }
                    // Fetch the deal metrics from Audited Financials
                    getCompanyDealMetrics({
                        requestPayload: {
                            requestId: getTimeStamp(),
                            parentOrgId: user.userData.investorOrgId,
                            orgId: urlParams.id as string,
                            type: 'auditedfinancials',
                            startDate: null,
                            endDate: null,
                            frequency: 'Yearly',
                            metricList: AUDITED_FINANCIAL_KEY_METRIC,
                            taskStepsList: ['AION_AUDITED_FINANCIAL'],
                            mis: 'Balance Sheet',
                            raw: true,
                            classificationId: 30,
                        },
                    })
                        .then((dealData) => {
                            setLoadingPrefilledData(false);

                            const borrowerFinancialData = dealData?.borrowerFinancials ?? {};
                            const borrowerProfileData = dealData?.borrowerProfile ?? {};
                            const financialYear = dealData?.financialYear || null;

                            // Auto the missing metrics in borrowerProfile with AICA Report data, else take it from the input metrics
                            const mergedBorrowerProfile = { ...borrowerProfile };

                            Object.keys(borrowerProfileData).forEach((key) => {
                                const isCompanyMetricsMissing =
                                    borrowerProfile[key] === undefined || borrowerProfile[key] === null || borrowerProfile[key] === '';
                                if (isCompanyMetricsMissing) {
                                    mergedBorrowerProfile[key] = borrowerProfileData[key];
                                }
                            });

                            // Pre-fill LinkedIn from the enriched socials Overview already
                            // fetched (Redux), but only when the user has no saved value —
                            // a manual edit always wins.
                            const enrichedLinkedin = investorData?.enrichedLinkedin;
                            if (!mergedBorrowerProfile[BORROWER_PROFILE_CONSTANTS.LINKEDIN_KEY] && enrichedLinkedin) {
                                mergedBorrowerProfile[BORROWER_PROFILE_CONSTANTS.LINKEDIN_KEY] = enrichedLinkedin;
                            }

                            const finalMetrics = {
                                ...data,
                                borrowerProfile: mergedBorrowerProfile,
                            };

                            if (financialYear) {
                                const newFinancialYearMapping = { ...auditedfinancialsMapping };
                                Object.keys(borrowerFinancialData).forEach((key) => {
                                    if (borrowerFinancialData[key] !== null) {
                                        newFinancialYearMapping[`borrowerFinancials.${key}`] = {
                                            // year: financialYear,
                                            value: borrowerFinancialData[key],
                                        };
                                    }
                                });
                                if (borrowerFinancials.latestConsolidatedFY !== undefined) {
                                    newFinancialYearMapping['borrowerFinancials.latestConsolidatedFY'] = {
                                        value: borrowerFinancials.latestConsolidatedFY,
                                    };
                                }
                                if (borrowerFinancials.latestStandaloneFY !== undefined) {
                                    newFinancialYearMapping['borrowerFinancials.latestStandaloneFY'] = {
                                        value: borrowerFinancials.latestStandaloneFY,
                                    };
                                }
                                Object.keys(borrowerProfileData).forEach((key) => {
                                    if (borrowerProfileData[key] !== null) {
                                        newFinancialYearMapping[`borrowerProfile.${key}`] = {
                                            // year: financialYear,
                                            value: borrowerProfileData[key],
                                        };
                                    }
                                });

                                // Add Year to the mapping for audited financials
                                newFinancialYearMapping['borrowerFinancials.dataAsOfDate'] = {
                                    value: `${FY_END_DATE} ${(
                                        getFinancialYear(Number(getAuditedYear(financialYear)), false, true) ?? ''
                                    ).toString()}`,
                                };
                                newFinancialYearMapping['financialYear'] = {
                                    value: (getFinancialYear(Number(financialYear), true) ?? '').toString(),
                                };
                                setAuditedfinancialsMapping(newFinancialYearMapping);
                            }
                            const flattenedData = flatten(finalMetrics);
                            setFormData(flattenedData);
                            setOriginalFormData(flattenedData);
                            setPreFilledDataFetched(true);
                        })
                        .catch((error) => {
                            console.error('Error fetching deal data:', error);
                            setLoadingPrefilledData(false);
                        });
                })
                .catch((error) => {
                    console.error('Error fetching initial data:', error);
                    setLoadingPrefilledData(false);
                });
        }
        // if normal deal flow, e.g. step = BORROWER_PROFILE
        else if (
            formConfig?.[step - 1]?.step === CONFIG_STEP.BORROWER_PROFILE &&
            isEmpty(investorData?.dealDrawer?.dealId) &&
            isEmpty(investorData?.dealDrawer?.lenderDealId) &&
            !preFilledDataFetched
        ) {
            const [from, to] = initializeDatepickerDates('Yearly');
            setLoadingPrefilledData(true);
            dispatch(
                getMetricsForDealCreation(
                    {
                        parentOrgId: platformData.investorOrgId,
                        orgId: investorData?.dealDrawer?.companyId as string,
                        startDate: from,
                        endDate: to,
                    },
                    {
                        onSuccess: (metricsData) => {
                            setLoadingPrefilledData(false);
                            const flattenedData = flatten(metricsData);
                            setFormData(flattenedData);
                            setOriginalFormData(flattenedData);
                            setPreFilledDataFetched(true);
                        },
                        onError: () => {
                            setLoadingPrefilledData(false);
                        },
                    },
                ),
            );
        }

        // If step = SELECT_INVESTORS
        // else if (formConfig?.[step - 1]?.step === CONFIG_STEP.SELECT_INVESTORS) {
        //     const createDealPayload: any = unflatten(formData);
        //     createDealPayload.dealProposerId = platformData?.investorOrgId;
        //     createDealPayload.dealBeneficiaryId = dealDrawerState?.companyId;
        //     createDealPayload.dealCreatedBy = loggedInUser?.userName;
        //     if (createDealPayload.dealTerms?.dealNotes) {
        //         createDealPayload.dealTerms.dealNotes = [
        //             {
        //                 note: createDealPayload.dealTerms.dealNotes,
        //                 noteBy: loggedInUser?.userName,
        //                 noteDate: new Date().toISOString(),
        //             },
        //         ];
        //     }
        //     dispatch(
        //         getPecMatchingData(createDealPayload, {
        //             onSuccess: (pecMatchingData) => {
        //                 setLenderList(pecMatchingData);
        //             },
        //         }),
        //     );
        // }
    }, [
        formConfig,
        step,
        investorData?.dealDrawer,
        debtProductFilters,
        source,
        preFilledDataFetched,
        platformData,
        loggedInUser,
        dispatch,
        urlParams,
    ]);

    // Optional for animation class
    useEffect(() => {
        if (animationClass) {
            const timer = setTimeout(() => {
                setAnimationClass('fade-in');
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [animationClass]);

    // -------------------------------------
    // RETURN HOOK DATA
    // -------------------------------------
    return {
        // State
        formData,
        setFormData,
        step,
        disablePrimary,
        disableSecondary,
        animationClass,
        loadingPrefilledData,
        formConfig,
        stepperConfig,
        investorName,
        lenderList,
        loanTypeOptions,
        sectorList,
        negativeSectorList,
        dealDrawerState: investorData?.dealDrawer,
        isFromComplete,
        source,
        isWebsiteValid,
        setIsWebsiteValid,
        isLinkedInValid,
        isWebsitePrefilled,
        showDismissalAlert,
        hasUnsavedChanges,
        activeFinancialsTab,

        // PEC field selection
        pecStepSection,
        pecFilteredSection,
        pecPickerOptions,
        pecSelectedFields,
        pecAlwaysOnFields,
        isStepConfigured,

        // Refs
        dealDrawerBodyRef,
        stepContainerRef,

        // Methods
        closeDrawer,
        handlePrimary,
        handleSecondary,
        handleStepperClick,
        setDisablePrimary,
        handleChange,
        handlePickerSelectionChange,
        handleClearField,
        handleRemoveAllForStep,
        handleClearAllForStep,
        auditedfinancialsMapping,
        getInputValidation,
        getButtonIcon,
        getButtonText,
        validateWebsite,
        negativeLenderList,
        connectedLenderNames,
        getConvertedValue,
        navigateStep,
        companyMetricsUpdate,
        continueToNextStep,
        params,
        setParams,
        handleUpdateMandate,
        handleSkipMandate,
        handleDrawerClose,
        handleSaveAndClose,
        handleCloseWithoutSave,
        setShowDismissalAlert,
        citiesList,
        handleFinancialsTabChange,
        showRemoveAllConfirm,
        setShowRemoveAllConfirm,
        isPickerOpen,
        setIsPickerOpen,

        // Application Form drawer (MANDATE_APPLICATION_FORM source)
        coApplicants,
        removeCoApplicantRow,
        updateCoApplicantField,
        primaryApplicantLink,
        primarySlotActive,
        existingDirectors,
        applicantPickerSelectedIds,
        handleApplicantPickerChange,
        handleAddApplicant,
        clearPrimaryApplicant,
        applicantPrimaryTypeError,
        duplicateApplicantError,
    };
}
